import React, { useState, useEffect, useMemo, useRef } from "react";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import { SearchIcon, X, Info, Inbox, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import useAlpacaStraddleStore from "../../../contexts/LTPStore/useAlpacaStraddleStore";
import WebSocketManager from "../../../contexts/websocketManager";

const API = server.godseye.baseUrl.replace(/\/$/, "");

// ─────────────────────────────────────────────────────────
// US Straddle Row Component with Live Price Flash
// ─────────────────────────────────────────────────────────
const USStraddleRow = React.memo(({ row, index, formatLargeNumber, formatDollarValue }) => {
    const prevPrice = useRef(row.underlying_price);
    const prevStraddleCost = useRef(row.straddle_cost ?? row.straddle_mid);
    const prevCeLast = useRef(row.ce_last);

    const priceRef = useRef(null);
    const straddleRef = useRef(null);
    const cePeRef = useRef(null);

    const flash = (ref) => {
        if (!ref.current) return;
        ref.current.classList.add("price-flash");
        setTimeout(() => ref.current?.classList.remove("price-flash"), 900);
    };

    useEffect(() => {
        if (prevPrice.current !== row.underlying_price) {
            flash(priceRef);
            prevPrice.current = row.underlying_price;
        }
    }, [row.underlying_price]);

    useEffect(() => {
        const currentCost = row.straddle_cost ?? row.straddle_mid;
        if (prevStraddleCost.current !== currentCost) {
            flash(straddleRef);
            prevStraddleCost.current = currentCost;
        }
    }, [row.straddle_cost, row.straddle_mid]);

    useEffect(() => {
        const currentCe = row.ce_last ?? row.ce_mid;
        const currentPe = row.pe_last ?? row.pe_mid;
        const combined = `${currentCe}-${currentPe}`;
        if (prevCeLast.current !== combined) {
            flash(cePeRef);
            prevCeLast.current = combined;
        }
    }, [row.ce_last, row.pe_last, row.ce_mid, row.pe_mid]);

    const costPct = row.cost_pct ?? row.straddle_mid_pct;
    const formattedPct = costPct !== null && costPct !== undefined ? `${costPct}%` : 'N/A';

    return (
        <tr className="hover:bg-white/5 transition-colors">
            <td className="p-3 text-zinc-400 font-medium">{index + 1}</td>
            <td className="p-3 font-semibold text-emerald-400">{row.symbol}</td>
            <td ref={priceRef} className="p-3 text-right font-medium text-white transition-colors duration-200">
                ${row.underlying_price != null ? Number(row.underlying_price).toFixed(2) : 'N/A'}
            </td>
            <td className="p-3 text-right text-zinc-300">${row.atm_strike != null ? Number(row.atm_strike).toFixed(2) : 'N/A'}</td>
            <td className="p-3 text-right text-zinc-300">{row.dte != null ? `${row.dte}d` : 'N/A'}</td>
            <td className="p-3 text-left font-mono text-[10px] whitespace-nowrap">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                        <span className="bg-emerald-500/10 text-emerald-400 px-1 rounded text-[9px] font-bold">C</span>
                        <span className="text-zinc-300">{row.ce_symbol || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="bg-rose-500/10 text-rose-400 px-1 rounded text-[9px] font-bold">P</span>
                        <span className="text-zinc-300">{row.pe_symbol || 'N/A'}</span>
                    </div>
                </div>
            </td>
            <td ref={cePeRef} className="p-3 text-right text-zinc-400 transition-colors duration-200">
                ${row.ce_last != null ? Number(row.ce_last).toFixed(2) : row.ce_mid != null ? Number(row.ce_mid).toFixed(2) : 'N/A'} / ${row.pe_last != null ? Number(row.pe_last).toFixed(2) : row.pe_mid != null ? Number(row.pe_mid).toFixed(2) : 'N/A'}
            </td>
            <td ref={straddleRef} className="p-3 text-right font-bold text-white transition-colors duration-200">
                ${row.straddle_cost != null ? Number(row.straddle_cost).toFixed(2) : row.straddle_mid != null ? Number(row.straddle_mid).toFixed(2) : 'N/A'}
            </td>
            <td className={`p-3 text-right font-extrabold ${costPct > 15 ? 'text-rose-400' : costPct > 8 ? 'text-amber-400' : 'text-zinc-300'}`}>
                {formattedPct}
            </td>
            <td className="p-3 text-right whitespace-nowrap">
                {(() => {
                    const cePct = row.ce_spread_pct !== null && row.ce_spread_pct !== undefined ? Number(row.ce_spread_pct) : null;
                    const pePct = row.pe_spread_pct !== null && row.pe_spread_pct !== undefined ? Number(row.pe_spread_pct) : null;
                    const ceColor = cePct === null ? 'text-zinc-600' : cePct < 5 ? 'text-emerald-400' : cePct < 15 ? 'text-amber-400' : 'text-rose-400';
                    const peColor = pePct === null ? 'text-zinc-600' : pePct < 5 ? 'text-emerald-400' : pePct < 15 ? 'text-amber-400' : 'text-rose-400';
                    return (
                        <span className="font-semibold text-[11px]">
                            <span className={ceColor}>{cePct !== null ? `${cePct.toFixed(2)}%` : '–'}</span>
                            <span className="text-zinc-500"> / </span>
                            <span className={peColor}>{pePct !== null ? `${pePct.toFixed(2)}%` : '–'}</span>
                        </span>
                    );
                })()}
            </td>
            <td className="p-3 text-right text-zinc-400 font-mono text-[11px]">
                {row.ce_bid_size != null ? row.ce_bid_size : '-'} / {row.pe_bid_size != null ? row.pe_bid_size : '-'}
            </td>
            <td className="p-3 text-right text-zinc-400 font-mono text-[11px]">
                {row.ce_ask_size != null ? row.ce_ask_size : '-'} / {row.pe_ask_size != null ? row.pe_ask_size : '-'}
            </td>
            <td className="p-3 text-right text-zinc-400">{row.ce_iv != null ? `${(Number(row.ce_iv) * 100).toFixed(1)}%` : 'N/A'}</td>
            <td className="p-3 text-right text-zinc-400">{row.pe_iv != null ? `${(Number(row.pe_iv) * 100).toFixed(1)}%` : 'N/A'}</td>
            <td className="p-3 text-right text-zinc-500">{row.ce_volume != null ? row.ce_volume : '-'} / {row.pe_volume != null ? row.pe_volume : '-'}</td>
            <td className="p-3 text-right text-zinc-500">{row.ce_open_interest != null ? row.ce_open_interest : '-'} / {row.pe_open_interest != null ? row.pe_open_interest : '-'}</td>
            <td className="p-3 text-right text-zinc-400">{row.stock_spread_pct != null ? `${Number(row.stock_spread_pct).toFixed(2)}%` : "-"}</td>
        </tr>
    );
});
USStraddleRow.displayName = "USStraddleRow";

// ─────────────────────────────────────────────────────────
// Main US Option Screener View
// ─────────────────────────────────────────────────────────
const USOptionScreenerView = () => {
    // Live Realtime WebSocket Store
    const { straddles: wsStraddles, lastUpdated } = useAlpacaStraddleStore();

    // Fallback REST State
    const [apiStraddles, setApiStraddles] = useState([]);
    const [screenerLoading, setScreenerLoading] = useState(false);
    const [screenerQuery, setScreenerQuery] = useState("");
    const [screenerSortBy, setScreenerSortBy] = useState("symbol");
    const [screenerSortDesc, setScreenerSortDesc] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Pagination and Mode states
    const [screenerPage, setScreenerPage] = useState(1);
    const [screenerPageSize, setScreenerPageSize] = useState(100);
    const [screenerTotalRecords, setScreenerTotalRecords] = useState(0);
    const [screenerTotalPages, setScreenerTotalPages] = useState(1);
    const [screenerMode, setScreenerMode] = useState("all"); // "all" or "top100"
    const [debouncedScreenerQuery, setDebouncedScreenerQuery] = useState("");

    // Liquidity filter: null = All, 5 = Tight (<5%), 15 = Moderate (<15%)
    const [liquidityFilter, setLiquidityFilter] = useState(null);

    const LIQUIDITY_PRESETS = [
        { label: "All", value: null, color: "text-zinc-400", activeBg: "bg-zinc-700/60 text-white border-zinc-500/50" },
        { label: "🟢 Liquid (<5%)", value: 5, color: "text-emerald-400", activeBg: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
        { label: "🟡 Moderate (<15%)", value: 15, color: "text-amber-400", activeBg: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
    ];

    // Connect Global WebSocket Stream on Mount
    useEffect(() => {
        WebSocketManager.getInstance().connectGlobalLtp();
    }, []);

    // Debounce screener search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedScreenerQuery(screenerQuery);
            setScreenerPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [screenerQuery]);

    // Fallback REST fetch if WS stream has no active data yet
    const fetchStraddles = async () => {
        if (wsStraddles.length > 0) return; // Skip API fetch if WS data is active
        setScreenerLoading(true);
        try {
            let params = {};
            if (screenerMode === "top100") {
                params = {
                    page: 1,
                    page_size: 100,
                    sort_by: "straddle_mid_pct",
                    sort_desc: true
                };
            } else {
                params = {
                    page: screenerPage,
                    page_size: screenerPageSize,
                    sort_by: screenerSortBy,
                    sort_desc: screenerSortDesc,
                    search: debouncedScreenerQuery || undefined,
                    ...(liquidityFilter !== null ? { max_spread_pct: liquidityFilter } : {})
                };
            }

            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, val]) => {
                if (val !== undefined && val !== null) {
                    queryParams.append(key, String(val));
                }
            });

            const res = await apiClient.get(`${API}/alpaca/straddle-costs?${queryParams.toString()}`);
            if (res.data?.data) {
                setApiStraddles(res.data.data);
                setScreenerTotalRecords(res.data.total_records || 0);
                setScreenerTotalPages(res.data.total_pages || 1);
            }
        } catch (error) {
            console.error("Failed to fetch straddle costs:", error);
            toast.error("Failed to load straddle costs");
        } finally {
            setScreenerLoading(false);
        }
    };

    useEffect(() => {
        if (wsStraddles.length === 0) {
            fetchStraddles();
        }
    }, [wsStraddles.length, screenerPage, screenerPageSize, screenerSortBy, screenerSortDesc, screenerMode, debouncedScreenerQuery, liquidityFilter]);

    const triggerRefresh = async () => {
        setRefreshing(true);
        try {
            await apiClient.post(`${API}/alpaca/refresh-straddles?max_symbols=150`);
            toast.success("Option straddle refresh triggered in background! Loading updated data shortly.");
        } catch (error) {
            console.error("Failed to trigger refresh:", error);
            toast.error("Failed to trigger calculations refresh");
        } finally {
            setRefreshing(false);
        }
    };

    const handleSort = (col) => {
        if (screenerSortBy === col) {
            setScreenerSortDesc(!screenerSortDesc);
        } else {
            setScreenerSortBy(col);
            setScreenerSortDesc(true);
            if (col === "ce_spread_pct" && liquidityFilter === null) {
                setLiquidityFilter(5);
            }
        }
    };

    // Client-side Filter, Search, and Sort when using WS Live Stream
    const finalStraddles = useMemo(() => {
        const rawSource = wsStraddles.length > 0 ? wsStraddles : apiStraddles;
        let list = [...rawSource];

        // Search Filter
        if (debouncedScreenerQuery) {
            const q = debouncedScreenerQuery.toLowerCase();
            list = list.filter(s =>
                s.symbol?.toLowerCase().includes(q) ||
                s.asset_name?.toLowerCase().includes(q) ||
                s.ce_symbol?.toLowerCase().includes(q) ||
                s.pe_symbol?.toLowerCase().includes(q)
            );
        }

        // Liquidity Filter
        if (liquidityFilter !== null) {
            list = list.filter(s => {
                const ceSpread = s.ce_spread_pct != null ? Number(s.ce_spread_pct) : 999;
                const peSpread = s.pe_spread_pct != null ? Number(s.pe_spread_pct) : 999;
                return ceSpread <= liquidityFilter && peSpread <= liquidityFilter;
            });
        }

        // Top 100 Mode Override
        if (screenerMode === "top100") {
            list.sort((a, b) => (b.straddle_mid_pct || 0) - (a.straddle_mid_pct || 0));
            return list.slice(0, 100);
        }

        // In-memory Sorting
        if (screenerSortBy) {
            list.sort((a, b) => {
                let valA = a[screenerSortBy];
                let valB = b[screenerSortBy];

                if (valA == null) return screenerSortDesc ? 1 : -1;
                if (valB == null) return screenerSortDesc ? -1 : 1;

                if (typeof valA === "string") {
                    valA = valA.toLowerCase();
                    valB = (valB || "").toLowerCase();
                }

                if (valA < valB) return screenerSortDesc ? 1 : -1;
                if (valA > valB) return screenerSortDesc ? -1 : 1;
                return 0;
            });
        }

        return list;
    }, [wsStraddles, apiStraddles, debouncedScreenerQuery, liquidityFilter, screenerMode, screenerSortBy, screenerSortDesc]);

    const formatLargeNumber = (num) => {
        if (num === null || num === undefined) return "-";
        const val = Number(num);
        if (val >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
        if (val >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
        if (val >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
        return val.toLocaleString('en-US');
    };

    const formatDollarValue = (num) => {
        if (num === null || num === undefined) return "-";
        const val = Number(num);
        if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
        if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
        if (val >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
        return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-[#14233a] border border-[#1a2b4c] rounded-xl p-4 shadow-lg text-white">
            {/* Screener Sub-Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 border-b border-white/5 pb-3">
                <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-md select-none">
                        US Option Screener
                    </span>

                    {wsStraddles.length > 0 && (
                        <span className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-md border border-emerald-400/20 text-emerald-400 bg-emerald-400/10 animate-pulse select-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                            LIVE REALTIME
                        </span>
                    )}

                    {lastUpdated && (
                        <span className="text-[10px] text-zinc-500 font-mono">
                            Last tick: {new Date(lastUpdated).toLocaleTimeString()}
                        </span>
                    )}

                    <div className="flex items-center gap-1 bg-[#0d0f18]/50 p-1 rounded-lg border border-white/5">
                        <button
                            onClick={() => {
                                setScreenerMode("all");
                                setScreenerPage(1);
                            }}
                            className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all ${screenerMode === "all"
                                    ? "bg-[#14233a] text-white shadow-sm"
                                    : "text-zinc-400 hover:text-white"
                                }`}
                        >
                            All Stocks
                        </button>
                        <button
                            onClick={() => {
                                setScreenerMode("top100");
                                setScreenerPage(1);
                            }}
                            className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all ${screenerMode === "top100"
                                    ? "bg-[#14233a] text-white shadow-sm"
                                    : "text-zinc-400 hover:text-white"
                                }`}
                        >
                            Top 100 by Cost %
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Search Input */}
                    <div className="relative">
                        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input
                            type="text"
                            placeholder="Search US Stock symbol..."
                            value={screenerQuery}
                            onChange={(e) => setScreenerQuery(e.target.value)}
                            className="bg-[#22272b] border border-transparent focus:border-zinc-650 rounded-lg pl-9 pr-8 py-1.5 text-xs text-[#b6c2cf] focus:outline-none transition-all placeholder:text-zinc-500 w-64 shadow-inner"
                        />
                        {screenerQuery && (
                            <button
                                onClick={() => setScreenerQuery("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <button onClick={triggerRefresh} disabled={refreshing}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-zinc-300 hover:text-white transition-all disabled:opacity-50">
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Liquidity Filter Bar */}
            {screenerMode === "all" && (
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 select-none">Liquidity Filter:</span>
                    {LIQUIDITY_PRESETS.map((preset) => (
                        <button
                            key={String(preset.value)}
                            onClick={() => { setLiquidityFilter(preset.value); setScreenerPage(1); }}
                            className={`px-2.5 py-1 rounded-md text-[11px] font-bold border transition-all ${liquidityFilter === preset.value
                                    ? preset.activeBg
                                    : "bg-transparent border-white/10 text-zinc-500 hover:text-zinc-300 hover:border-white/20"
                                }`}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Main Table */}
            <div className="flex-1 flex flex-col min-h-0 pr-1">
                {screenerLoading && wsStraddles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                        <div className="w-8 h-8 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                        <span className="text-xs text-zinc-500 font-bold uppercase tracking-widest">Loading Straddles...</span>
                    </div>
                ) : finalStraddles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
                        <Inbox className="w-12 h-12 text-zinc-700 mb-3" />
                        <p className="text-sm font-semibold">No Straddle Data Available</p>
                        <p className="text-xs text-zinc-600 mt-1">Run a refresh calculation or verify options stream worker.</p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-auto rounded-lg border border-white/5 bg-[#0d0f18]/60 custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-[#14233a] border-b border-white/10 text-zinc-400 text-xs font-semibold select-none">
                                    <th className="p-3 text-zinc-400 text-left whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">#</th>
                                    <th className="p-3 cursor-pointer hover:text-white whitespace-nowrap sticky top-0 z-10 bg-[#14233a]" onClick={() => handleSort("symbol")}>
                                        Symbol {screenerSortBy === "symbol" && (screenerSortDesc ? "↓" : "↑")}
                                    </th>
                                    <th className="p-3 cursor-pointer hover:text-white text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]" onClick={() => handleSort("underlying_price")}>
                                        Price {screenerSortBy === "underlying_price" && (screenerSortDesc ? "↓" : "↑")}
                                    </th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">Strike</th>
                                    <th className="p-3 cursor-pointer hover:text-white text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]" onClick={() => handleSort("dte")}>
                                        DTE {screenerSortBy === "dte" && (screenerSortDesc ? "↓" : "↑")}
                                    </th>
                                    <th className="p-3 text-left whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">CE / PE Symbol</th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">Call / Put LTP</th>
                                    <th className="p-3 cursor-pointer hover:text-white text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]" onClick={() => handleSort("straddle_mid")}>
                                        Straddle Cost {screenerSortBy === "straddle_mid" && (screenerSortDesc ? "↓" : "↑")}
                                    </th>
                                    <th className="p-3 cursor-pointer hover:text-white text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]" onClick={() => handleSort("straddle_mid_pct")}>
                                        Cost % {screenerSortBy === "straddle_mid_pct" && (screenerSortDesc ? "↓" : "↑")}
                                    </th>
                                    <th className="p-3 cursor-pointer hover:text-white text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]" onClick={() => handleSort("ce_spread_pct")}>
                                        Bid/Ask Diff % {screenerSortBy === "ce_spread_pct" && (screenerSortDesc ? "↓" : "↑")}
                                    </th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">Bid Size</th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">Ask Size</th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">CE IV</th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">PE IV</th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">Vol (C/P)</th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">OI (C/P)</th>
                                    <th className="p-3 text-right whitespace-nowrap sticky top-0 z-10 bg-[#14233a]">Stock Range %</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 text-xs text-zinc-300">
                                {finalStraddles.map((row, index) => (
                                    <USStraddleRow
                                        key={row.symbol || index}
                                        row={row}
                                        index={index}
                                        formatLargeNumber={formatLargeNumber}
                                        formatDollarValue={formatDollarValue}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default USOptionScreenerView;
